import React, { useState, useEffect, useContext } from 'react';
import { Card, Form, InputNumber, Input, Button, Table, Space, Popconfirm, Empty, Modal } from 'antd';
import { EditOutlined, SaveOutlined, CloseOutlined, DeleteOutlined } from '@ant-design/icons';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import { showSuccessMessage, showErrorMessage } from '../../services/messages';
import GeneralContext from '../../context/GeneralContext/GeneralContext';
import EUserTypes from '../../enums/EUserTypes';
import { ScreenHeader, QuantityStep, FLOW_COLORS, primaryButtonStyle, BackToHome } from '../../components/drum/WizardParts';
import JustificationModal, { JustificationInput, isJustificationValid } from '../../components/modals/JustificationModal';

/**
 * Painel de Amarelamento: registra quantos litros de solvente limpo viraram
 * solvente amarelo ao longo do dia.
 * - Cada amarelamento decrementa o disponível de Solvente e incrementa o de
 *   Solvente Amarelo (validado no back-end contra o disponível atual).
 * - Toda alteração gera AuditLog (SOLVENT_TURNOVER) no back-end.
 * - Não há MachineSelector: amarelamento é um evento de estoque, não por máquina.
 *
 * Correções (regra do cliente):
 * - Admin edita/exclui qualquer registro (justificativa opcional).
 * - Operador-comprador e Líder editam/excluem registros DE HOJE, sempre com
 *   justificativa obrigatória gravada na auditoria.
 * - Operador comum só inclui e visualiza.
 *
 * Prop `mobile` (opcional): quando true (operadores no PWA), UI vertical, inputs
 * full-width, lista apenas do dia atual como cards compactos.
 * Quando não passada, deriva de `!isAdmin`.
 */
const AmarelamentoPanel = ({ mobile: mobileProp }) => {
  const { loggedUser } = useContext(GeneralContext);
  const location = useLocation();
  const isAdmin = loggedUser?.type === EUserTypes.PANEL_ADMIN.code;
  const mobile = mobileProp !== undefined ? mobileProp : !isAdmin;
  const flowColor = location.state?.color || FLOW_COLORS.amarelamento;
  // Comprador/líder (e admin) podem corrigir registros de hoje pelo PWA.
  const canCorrect = EUserTypes.canEditToday(loggedUser);
  // Justificativa obrigatória para não-admin (validada também na API).
  const justRequired = !isAdmin;

  const [form] = Form.useForm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editRecord, setEditRecord] = useState(null);
  // Correção no PWA: modal de edição (campos + justificativa) e alvo de exclusão.
  const [editModal, setEditModal] = useState(null);
  const [editJustification, setEditJustification] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const isToday = (dateStr) => {
    const d = new Date(dateStr);
    const n = new Date();
    return d.toDateString() === n.toDateString();
  };

  const load = async () => {
    setLoading(true);
    try {
      // PWA (mobile) lista só hoje; admin desktop lista histórico paginado.
      const res = mobile
        ? await api.operation.listTurnoversToday()
        : await api.operation.listTurnovers({ limit: 50 });
      const l = res.data?.rows || res.data || [];
      setList(Array.isArray(l) ? l : []);
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao carregar amarelamentos.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [mobile]);

  const handleAdd = async (values) => {
    setSaving(true);
    try {
      await api.operation.createTurnover({
        liters: values.liters,
        observation: values.observation || null
      });
      showSuccessMessage('Amarelamento registrado!');
      form.resetFields();
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao registrar amarelamento.');
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    try {
      await api.operation.updateTurnover(id, editRecord);
      showSuccessMessage('Amarelamento atualizado!');
      setEditingId(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao atualizar amarelamento.');
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.operation.deleteTurnover(id);
      showSuccessMessage('Amarelamento removido!');
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover amarelamento.');
    }
  };

  /** Salva a edição feita no modal do PWA (campos + justificativa). */
  const handleMobileUpdate = async () => {
    if (!editModal) return;
    setSavingEdit(true);
    try {
      await api.operation.updateTurnover(editModal.id, { ...editRecord, justification: editJustification.trim() || undefined });
      showSuccessMessage('Amarelamento corrigido!');
      setEditModal(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao corrigir amarelamento.');
    } finally {
      setSavingEdit(false);
    }
  };

  /** Exclui via modal de justificativa (PWA). */
  const handleMobileDelete = async (justification) => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      await api.operation.deleteTurnover(deleteTarget.id, { justification: justification || undefined });
      showSuccessMessage('Amarelamento removido!');
      setDeleteTarget(null);
      load();
    } catch (e) {
      showErrorMessage(e?.response?.data?.error || 'Erro ao remover amarelamento.');
    } finally {
      setDeleting(false);
    }
  };

  /** Abre o modal de correção com os valores atuais do registro. */
  const openEditModal = (r) => {
    setEditRecord({ liters: Number(r.liters), observation: r.observation });
    setEditJustification('');
    setEditModal(r);
  };

  const columns = [
    { title: 'Data/hora', dataIndex: 'occurred_at', key: 'occurred_at', render: v => new Date(v).toLocaleString('pt-BR') },
    {
      title: 'Litros', key: 'liters',
      render: (_, r) => editingId === r.id ? (
        <InputNumber value={editRecord?.liters} onChange={v => setEditRecord(p => ({ ...p, liters: v }))} min={0.01} step={0.1} decimalSeparator="," />
      ) : r.liters
    },
    {
      title: 'Observação', key: 'observation',
      render: (_, r) => editingId === r.id ? (
        <Input value={editRecord?.observation || ''} onChange={e => setEditRecord(p => ({ ...p, observation: e.target.value }))} />
      ) : (r.observation || '—')
    },
    { title: 'Usuário', key: 'user', render: (_, r) => r.user?.full_name || '—' },
    {
      title: 'Ações', key: 'actions',
      render: (_, r) => {
        const canEdit = mobile ? isAdmin : (isAdmin || isToday(r.occurred_at));
        if (!canEdit) return null;
        if (editingId === r.id) {
          return (
            <Space>
              <Button size="small" type="primary" icon={<SaveOutlined />} onClick={() => handleUpdate(r.id)} />
              <Button size="small" icon={<CloseOutlined />} onClick={() => setEditingId(null)} />
            </Space>
          );
        }
        return (
          <Space>
            <Button size="small" icon={<EditOutlined />} onClick={() => { setEditingId(r.id); setEditRecord({ liters: r.liters, observation: r.observation, occurred_at: r.occurred_at }); }}>Editar</Button>
            <Popconfirm
              title="Excluir este amarelamento?"
              okText="Excluir"
              cancelText="Cancelar"
              okButtonProps={{ danger: true }}
              onConfirm={() => handleDelete(r.id)}
            >
              <Button size="small" danger icon={<DeleteOutlined />} />
            </Popconfirm>
          </Space>
        );
      }
    }
  ];

  const todaysList = list.filter((r) => isToday(r.occurred_at));

  // Modal de correção do PWA (comprador/líder/admin): campos + justificativa.
  const mobileEditModal = (
    <Modal
      title="Corrigir amarelamento"
      open={!!editModal}
      onCancel={() => setEditModal(null)}
      centered
      footer={[
        <Button key="cancel" onClick={() => setEditModal(null)}>Cancelar</Button>,
        <Button
          key="ok"
          type="primary"
          loading={savingEdit}
          disabled={!isJustificationValid(editJustification, justRequired)}
          onClick={handleMobileUpdate}
        >
          Salvar correção
        </Button>,
      ]}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Litros</div>
          <InputNumber
            value={editRecord?.liters}
            onChange={(v) => setEditRecord((p) => ({ ...p, liters: v }))}
            min={0.01}
            step={0.1}
            decimalSeparator=","
            style={{ width: '100%' }}
            size="large"
            aria-label="Litros do amarelamento"
          />
        </div>
        <div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 6 }}>Observação</div>
          <Input.TextArea
            value={editRecord?.observation || ''}
            onChange={(e) => setEditRecord((p) => ({ ...p, observation: e.target.value }))}
            autoSize={{ minRows: 2 }}
            aria-label="Observação do amarelamento"
          />
        </div>
        <JustificationInput value={editJustification} onChange={setEditJustification} required={justRequired} />
      </div>
    </Modal>
  );

  const mobileDeleteModal = (
    <JustificationModal
      open={!!deleteTarget}
      title="Excluir amarelamento"
      description={deleteTarget ? (
        <span>Excluir o amarelamento de <strong>{deleteTarget.liters} L</strong>?</span>
      ) : null}
      confirmLabel="Excluir"
      danger
      loading={deleting}
      required={justRequired}
      onConfirm={handleMobileDelete}
      onCancel={() => setDeleteTarget(null)}
    />
  );

  if (mobile) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <BackToHome color={flowColor} />
        <Card loading={loading}>
          <ScreenHeader
            title="Registrar amarelamento"
            subtitle="Solvente limpo que virou Solvente Amarelo."
            color={flowColor}
          />
          <Form form={form} layout="vertical" onFinish={handleAdd}>
            <QuantityStep
              form={form}
              name="liters"
              label="Litros que amarelaram"
              hint="Quantidade de solvente limpo que virou amarelo."
            />
            <Form.Item label="Observação" name="observation">
              <Input.TextArea autoSize={{ minRows: 2 }} placeholder="Opcional" />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" loading={saving} size="large" block style={primaryButtonStyle(flowColor)}>
                Registrar amarelamento
              </Button>
            </Form.Item>
          </Form>
        </Card>

        <Card loading={loading}>
          <ScreenHeader title="Amarelamentos de hoje" subtitle="Registros do dia atual." color={flowColor} />
          {todaysList.length === 0 ? (
            <Empty description="Nenhum amarelamento registrado hoje." />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {todaysList.map((r) => (
                <div
                  key={r.id}
                  style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: 12,
                    padding: '12px 14px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 4,
                    background: '#fafbff',
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <strong style={{ fontSize: 18 }}>{r.liters} L</strong>
                    {canCorrect && (
                      <Space size={4}>
                        <Button size="small" icon={<EditOutlined />} onClick={() => openEditModal(r)} aria-label="Corrigir amarelamento" />
                        <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setDeleteTarget(r)} aria-label="Excluir amarelamento" />
                      </Space>
                    )}
                  </div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {new Date(r.occurred_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                    {r.user ? ` · ${r.user.full_name}` : ''}
                    {r.observation ? ` · ${r.observation}` : ''}
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>

        {mobileEditModal}
        {mobileDeleteModal}
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
      <Card title="Registrar amarelamento" size="small">
        <Form form={form} layout="inline" onFinish={handleAdd}>
          <Form.Item label="Litros" name="liters" rules={[{ required: true, message: 'Informe os litros.' }]}>
            <InputNumber min={0.01} step={0.1} decimalSeparator="," style={{ width: 140 }} />
          </Form.Item>
          <Form.Item label="Observação" name="observation">
            <Input style={{ width: 240 }} />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={saving}>Registrar</Button>
          </Form.Item>
        </Form>
      </Card>

      <Card title="Amarelamentos recentes" size="small">
        <Table rowKey="id" size="small" loading={loading} dataSource={list} columns={columns} pagination={{ pageSize: 10 }} />
      </Card>
    </div>
  );
};

export default AmarelamentoPanel;
